import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { signOut } from "@/lib/auth";
import { getMessageFromError } from "@/lib/utils";
import * as Sentry from "@sentry/react";
import { Component, ErrorInfo, ReactNode } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: unknown;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    // Log the error to Sentry
    Sentry.captureReactException(error, errorInfo);
  }

  handleSignOut = async () => {
    try {
      await signOut();
    } catch (error: unknown) {
      toast.error("Error al cerrar sesión", {
        description: getMessageFromError(error as Error),
      });
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const error = this.state.error;
    const errorMessage = (() => {
      if (error instanceof Error) {
        return getMessageFromError(error);
      } else if (typeof error === "string") {
        return error;
      }
      return "Error desconocido";
    })();

    return (
      <Card className="mx-auto w-full max-w-2xl overflow-auto rounded-3xl p-4">
        <CardHeader>
          <CardTitle>Ocurrió un error inesperado</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-balance">{errorMessage}</p>

          {import.meta.env.DEV && (
            <details>
              <summary className="mb-2 font-bold">
                Detalles técnicos (solo en desarrollo)
              </summary>
              <pre className="whitespace-pre-wrap text-left text-xs">
                {error instanceof Error
                  ? error.stack
                  : JSON.stringify(error, null, 2)}
              </pre>
            </details>
          )}

          <p className="mt-6 flex flex-col items-center justify-center">
            <Button
              variant="secondary"
              className="mb-2"
              onClick={this.handleReload}
            >
              Recargar la página
            </Button>
            <Button variant="link" asChild>
              <Link to="/">Ir a la página principal</Link>
            </Button>
            <Button variant="link" onClick={this.handleSignOut}>
              Cerrar sesión
            </Button>
          </p>
        </CardContent>
      </Card>
    );
  }
}

export default ErrorBoundary;
